<script setup lang="ts">
import { api } from '@/api';
import {
  type InstallationConfigDto,
  type UpdateSiteNameRequestDto,
  UpdateSiteNameRequestDtoTypeEnum,
} from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import InstallationName from '@/components/InstallationName/InstallationName.vue';
import { success as successIcon } from '@/components/icons/icons';
import {
  type CompletedEventArgs,
  type CompletedStep,
  completedStepsOrdered,
} from '@/views/InstallationView/types';
import { ref } from 'vue';
import InstallationStep from './InstallationStep.vue';
import { formBackendErrorHandler } from '@/errorHandling';

interface InstallationStepInstallationNameProps {
  customerProvidedOptionalSiteName: NonNullable<
    InstallationConfigDto['customerProvidedOptionalSiteName']
  >;
  completedStep: CompletedStep;
}

const props = defineProps<InstallationStepInstallationNameProps>();
const emit = defineEmits<{
  completed: [args: CompletedEventArgs];
}>();

const showSuccessMessage = ref(false);

const { form } = useVeeForm<UpdateSiteNameRequestDto>({
  options: {
    initialValues: {
      customerProvidedOptionalSiteName: props.customerProvidedOptionalSiteName,
      $type:
        UpdateSiteNameRequestDtoTypeEnum.PtwPtw100AppZeroApplicationSharedInstallDtoUpdateSiteNameRequestDtoPtwPtw100AppZeroApplicationShared,
    },
  },
  schemaName: 'UpdateSiteNameRequestDto',
  mode: 'edit',
});

const submit = form.handleSubmit(
  async (updateSiteNameRequestDto) => {
    showSuccessMessage.value = false;
    const { success } = await api.installApi.updateSiteName(
      {
        updateSiteNameRequestDto,
      },
      [formBackendErrorHandler(form)],
    );
    if (success) {
      emit('completed', { shouldUpdate: true, preventNextNavigation: true });
      showSuccessMessage.value = true;
    }
  },
  {
    onSubmitValidationErrorCb: () => {
      showSuccessMessage.value = false;
    },
  },
);
</script>

<template>
  <form @submit="submit">
    <InstallationStep>
      <template #content>
        <InstallationName class="w-lg-50" />
      </template>
      <template #footer>
        <v-alert v-if="showSuccessMessage" class="mt-8 fit-content" type="success" :icon="false">
          <template #text>
            <v-icon class="mr-2" :icon="successIcon"></v-icon>
            <span class="font-weight-semibold">{{ $t('InstallationNameSaved') }}</span>
          </template>
        </v-alert>
        <VSpacer />
        <VeeSubmitButton class="ml-2 mt-8" :text="$t('Save')" />
        <Button
          variant="secondary"
          class="ml-2 mt-8"
          :disabled="completedStepsOrdered.indexOf(completedStep) <= 1"
          :label="$t('Next')"
          @click="
            emit('completed', {
              shouldUpdate: false,
              preventNextNavigation: false,
            })
          "
        />
      </template>
    </InstallationStep>
  </form>
</template>
